import { z } from '@hono/zod-openapi'

export const WebhookProviderSchema = z.enum([
  'outlook', 'gmail', 'sendgrid', 'ses', 'twilio', 'docusign',
  'adobe-sign', 'clever', 'oneroster', 'generic',
]).openapi({ example: 'outlook' })

export const WebhookSignatureSchema = z.object({
  algorithm: z.string().openapi({ example: 'SHA256HMAC' }),
  keyId: z.string().nullable().openapi({ description: 'Key identifier for rotation' }),
  signature: z.string().openapi({ example: 'sha256=abc123def456' }),
  signedHeaders: z.array(z.string()).nullable().openapi({ description: 'Headers included in signature' }),
}).openapi('WebhookSignature')

export const IncomingWebhookEventSchema = z.object({
  id: z.string().uuid(),
  provider: WebhookProviderSchema,
  providerEventId: z.string().nullable().openapi({ example: 'Microsoft.Graph.Event-1234' }),
  eventType: z.string().openapi({ example: 'message.received' }),
  payload: z.unknown(),
  signature: WebhookSignatureSchema.nullable(),
  rawBody: z.string().nullable().openapi({ description: 'Raw request body for signature verification' }),
  headers: z.record(z.string()).nullable(),
  receivedAt: z.string().datetime(),
  status: z.enum(['received', 'verified', 'processed', 'failed']),
  errorMessage: z.string().nullable(),
}).openapi('IncomingWebhookEvent', { description: 'An incoming webhook event from an external provider' })

export const OutgoingWebhookEventSchema = z.object({
  id: z.string().uuid(),
  districtId: z.string().min(1).max(64),
  destination: z.string().url().openapi({ example: 'https://hooks.district.edu/notify' }),
  eventType: z.string().openapi({ example: 'student.created' }),
  payload: z.record(z.unknown()),
  status: z.enum(['pending', 'delivered', 'failed', 'retrying']),
  attemptCount: z.number().int().default(0),
  maxAttempts: z.number().int().default(3),
  lastAttemptAt: z.string().datetime().nullable(),
  nextRetryAt: z.string().datetime().nullable(),
  lastResponseCode: z.number().int().nullable().openapi({ example: 200 }),
  lastErrorMessage: z.string().nullable(),
  createdAt: z.string().datetime(),
}).openapi('OutgoingWebhookEvent', { description: 'An outgoing webhook notification' })

export const WebhookSubscriptionSchema = z.object({
  id: z.string().uuid(),
  districtId: z.string().min(1).max(64),
  provider: WebhookProviderSchema,
  destination: z.string().url(),
  eventTypes: z.array(z.string()).min(1),
  secret: z.string().openapi({ description: 'HMAC signing secret (write-only)' }),
  active: z.boolean(),
  lastSuccessAt: z.string().datetime().nullable(),
  lastFailureAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
}).openapi('WebhookSubscription', { description: 'Registered webhook subscription' })

export type WebhookProvider = z.infer<typeof WebhookProviderSchema>
export type WebhookSignature = z.infer<typeof WebhookSignatureSchema>
export type IncomingWebhookEvent = z.infer<typeof IncomingWebhookEventSchema>
export type OutgoingWebhookEvent = z.infer<typeof OutgoingWebhookEventSchema>
export type WebhookSubscription = z.infer<typeof WebhookSubscriptionSchema>
